/**
 * header-search.js
 * Handles: search sidebar open/close, live predictive search via
 * Shopify's Predictive Search API, loading/empty/results states,
 * suggestion chips, and "recently viewed" (sessionStorage).
 *
 * Prices use the store's own money format (passed from Liquid on the
 * [data-search-sidebar] element), never a hard-coded "$".
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var RECENTLY_VIEWED_KEY = 'premiumHeaderRecentlyViewed';
  var RECENTLY_VIEWED_MAX = 4;
  var RECENTLY_VIEWED_STORE = 8;

  /* ---------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------- */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      window.clearTimeout(t);
      t = window.setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function escapeHTML(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function setState(root, state) {
    root.querySelectorAll('[data-search-state]').forEach(function (el) {
      el.hidden = el.dataset.searchState !== state;
    });
  }

  /**
   * Normalise a price to a decimal number in the shop's currency units.
   * - Predictive Search API (suggest.json) returns decimal strings: "2500.00"
   * - Liquid / product JSON returns integer cents: 250000
   */
  function toAmount(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return value / 100;
    var n = parseFloat(String(value).replace(/,/g, ''));
    return isNaN(n) ? null : n;
  }

  function addSeparators(amount, decimals, thousands, decimal) {
    var fixed = amount.toFixed(decimals);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
  }

  /** Shopify-style money formatter using the store's money format. */
  function formatMoney(amount, root) {
    if (amount === null) return '';
    var format = (root && root.dataset.moneyFormat) || '{{amount}}';
    var shopCurrency = root && root.dataset.shopCurrency;
    var activeCurrency = root && root.dataset.activeCurrency;

    // Customer is browsing in a different market currency → let the browser
    // format it correctly for that currency (e.g. AED, GBP, USD).
    if (activeCurrency && shopCurrency && activeCurrency !== shopCurrency && window.Intl) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: activeCurrency
        }).format(amount);
      } catch (e) { /* fall through to shop format */ }
    }

    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = addSeparators(amount, 0, ',', '.'); break;
      case 'amount_with_comma_separator':
        value = addSeparators(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = addSeparators(amount, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = addSeparators(amount, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator':
        value = addSeparators(amount, 0, ' ', ','); break;
      case 'amount_with_space_separator':
        value = addSeparators(amount, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator':
        value = addSeparators(amount, 2, ' ', '.'); break;
      default:
        value = addSeparators(amount, 2, ',', '.');
    }
    return match ? format.replace(match[0], value) : value;
  }

  function imageSrc(product) {
    var img = product.image || product.featured_image || (product.images && product.images[0]);
    if (!img) return '';
    if (typeof img === 'object') img = img.url || img.src || '';
    if (!img) return '';
    // Request a small, sharp thumbnail from Shopify's CDN.
    if (img.indexOf('cdn.shopify.com') !== -1 || img.indexOf('/cdn/shop/') !== -1) {
      img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=300';
    }
    return img;
  }

  function buildProductCardHTML(product, root) {
    var src = imageSrc(product);
    var price = toAmount(product.price != null ? product.price : product.price_min);
    var compare = toAmount(product.compare_at_price_max != null ? product.compare_at_price_max : product.compare_at_price);
    var onSale = compare !== null && price !== null && compare > price;
    var title = escapeHTML(product.title);

    return (
      '<a href="' + escapeHTML(product.url) + '" class="search-product-card">' +
        '<div class="search-product-card__image-wrap">' +
          (src
            ? '<img class="search-product-card__image search-product-card__image--main" src="' + escapeHTML(src) + '" alt="' + title + '" loading="lazy" width="300" height="300" onerror="this.style.display=\'none\'">'
            : '<div class="search-product-card__image-placeholder"></div>') +
          (onSale ? '<span class="search-product-card__badge">Sale</span>' : '') +
        '</div>' +
        '<div class="search-product-card__info">' +
          (product.vendor ? '<span class="search-product-card__vendor">' + escapeHTML(product.vendor) + '</span>' : '') +
          '<h4 class="search-product-card__title">' + title + '</h4>' +
          (price !== null
            ? '<span class="search-product-card__price">' +
                (onSale
                  ? '<span class="search-product-card__price--sale">' + formatMoney(price, root) + '</span>' +
                    '<span class="search-product-card__price--compare">' + formatMoney(compare, root) + '</span>'
                  : '<span>' + formatMoney(price, root) + '</span>') +
              '</span>'
            : '') +
        '</div>' +
      '</a>'
    );
  }

  /* ---------------------------------------------------------------------
   * Predictive search
   * ------------------------------------------------------------------- */
  function initSearchSidebar(root) {
    var input = root.querySelector('[data-search-input]');
    var productsGrid = root.querySelector('[data-products-grid]');
    var productsGroup = root.querySelector('[data-products-group]');
    var collectionsList = root.querySelector('[data-collections-list]');
    var collectionsGroup = root.querySelector('[data-collections-group]');
    var pagesList = root.querySelector('[data-pages-list]');
    var pagesGroup = root.querySelector('[data-pages-group]');
    var articlesList = root.querySelector('[data-articles-list]');
    var articlesGroup = root.querySelector('[data-articles-group]');
    var suggestionsList = root.querySelector('[data-suggestions-list]');
    var suggestionsGroup = root.querySelector('[data-suggestions-group]');

    if (!input) return;

    var currentController = null;
    var lastQuery = '';

    function renderList(listEl, groupEl, items, mapFn) {
      if (!listEl || !groupEl) return;
      if (!items || items.length === 0) {
        groupEl.hidden = true;
        listEl.innerHTML = '';
        return;
      }
      groupEl.hidden = false;
      listEl.innerHTML = items.map(mapFn).join('');
    }

    function linkItem(url, text) {
      return '<li><a href="' + escapeHTML(url) + '">' + escapeHTML(text) + '</a></li>';
    }

    function performSearch(query) {
      lastQuery = query;

      if (!query) {
        if (currentController) currentController.abort();
        setState(root, 'idle');
        return;
      }

      setState(root, 'loading');

      if (currentController) currentController.abort();
      currentController = new AbortController();

      var base = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      var params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,page,article,query',
        'resources[limit]': 6,
        'resources[options][unavailable_products]': 'last'
      });

      fetch(base + 'search/suggest.json?' + params.toString(), {
        signal: currentController.signal,
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Search request failed');
          return response.json();
        })
        .then(function (data) {
          // Ignore responses for a query the user has already changed.
          if (query !== lastQuery) return;

          var resources = (data && data.resources && data.resources.results) || {};
          var products = resources.products || [];
          var collections = resources.collections || [];
          var pages = resources.pages || [];
          var articles = resources.articles || [];
          var queries = resources.queries || [];

          if (!(products.length || collections.length || pages.length || articles.length || queries.length)) {
            setState(root, 'empty');
            return;
          }

          renderList(suggestionsList, suggestionsGroup, queries, function (q) {
            return linkItem(q.url, q.text);
          });

          if (productsGrid && productsGroup) {
            productsGroup.hidden = !products.length;
            productsGrid.innerHTML = products.map(function (p) {
              return buildProductCardHTML(p, root);
            }).join('');
          }

          renderList(collectionsList, collectionsGroup, collections, function (c) { return linkItem(c.url, c.title); });
          renderList(pagesList, pagesGroup, pages, function (p) { return linkItem(p.url, p.title); });
          renderList(articlesList, articlesGroup, articles, function (a) { return linkItem(a.url, a.title); });

          setState(root, 'results');
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          setState(root, 'empty');
        });
    }

    var debouncedSearch = debounce(function (event) {
      performSearch(event.target.value.trim());
    }, DEBOUNCE_MS);

    input.addEventListener('input', debouncedSearch);

    root.querySelectorAll('[data-search-suggestion]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        input.value = chip.dataset.searchSuggestion;
        input.focus();
        performSearch(input.value.trim());
      });
    });

    renderRecentlyViewed(root);
  }

  /* ---------------------------------------------------------------------
   * Recently viewed
   * ------------------------------------------------------------------- */
  function readRecentlyViewed() {
    try {
      var list = JSON.parse(window.sessionStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function renderRecentlyViewed(root) {
    var group = root.querySelector('[data-recently-viewed-group]');
    var grid = root.querySelector('[data-recently-viewed-grid]');
    if (!group || !grid) return;

    // Don't show the product the customer is currently looking at.
    var current = document.querySelector('[data-recently-viewed-product]');
    var currentUrl = null;
    if (current) {
      try { currentUrl = JSON.parse(current.textContent).url; } catch (e) { /* ignore */ }
    }

    var stored = readRecentlyViewed().filter(function (p) { return p && p.url && p.url !== currentUrl; });

    if (!stored.length) {
      group.hidden = true;
      grid.innerHTML = '';
      return;
    }

    group.hidden = false;
    grid.innerHTML = stored.slice(0, RECENTLY_VIEWED_MAX).map(function (p) {
      return buildProductCardHTML(p, root);
    }).join('');
  }

  // Record the current product page (JSON is printed by the search-sidebar snippet).
  function trackCurrentProduct() {
    var el = document.querySelector('[data-recently-viewed-product]');
    if (!el) return;
    var product;
    try { product = JSON.parse(el.textContent); } catch (e) { return; }
    if (!product || !product.url) return;

    var list = readRecentlyViewed().filter(function (p) { return p && p.url !== product.url; });
    list.unshift(product);
    try {
      window.sessionStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(list.slice(0, RECENTLY_VIEWED_STORE)));
    } catch (e) { /* storage full or blocked — ignore */ }
  }

  trackCurrentProduct();
  document.querySelectorAll('[data-search-sidebar]').forEach(initSearchSidebar);

  /* ---------------------------------------------------------------------
   * Open / close wiring
   * ------------------------------------------------------------------- */
  function drawerApi() {
    return window.PremiumHeader || null;
  }

  document.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var drawer = document.getElementById(btn.getAttribute('aria-controls'));
      var api = drawerApi();
      if (!drawer || !api) return;
      api.openDrawer(drawer, btn);
      var input = drawer.querySelector('[data-search-input]');
      if (input) window.setTimeout(function () { input.focus(); }, 350);
    });
  });

  document.querySelectorAll('[data-search-close]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var api = drawerApi();
      if (api) api.closeDrawer(btn.closest('[data-search-sidebar]'));
    });
  });

  document.querySelectorAll('[data-search-overlay]').forEach(function (overlay) {
    overlay.addEventListener('click', function () {
      var api = drawerApi();
      if (api) api.closeDrawer(overlay.closest('[data-search-sidebar]'));
    });
  });
})();
